import * as Icons from "lucide-react";
import {
  Wifi, Tv, UtensilsCrossed, ChefHat, Flame, Wind, Snowflake, Droplets, ParkingSquare,
  Coffee, Dumbbell, Dog, Baby, Cigarette, CigaretteOff, Flower2, BedDouble, Bath, ShowerHead,
  WashingMachine, Refrigerator, Microwave, Tv2, Speaker, Music4, Camera, Sparkles, Leaf,
  Lock, KeyRound, Car, Bike, MapPin, Briefcase, Laptop, Monitor, Sun, Cloud,
  AirVent, Flashlight, Mic, Book, Gamepad2, Zap, Trees, Home as HomeIcon,
} from "lucide-react";

/**
 * ICON_CATALOG — the visible master list of icons surfaced in the admin's icon
 * picker. Keep in sync with what amenities/highlights typically look like so
 * admins can pick something sensible in 1-2 clicks. Each entry is `[lucide-name, label]`.
 */
export const ICON_CATALOG = [
  ["Wifi", "Wi-Fi"],
  ["Tv", "TV"],
  ["Tv2", "Projector / Cinema"],
  ["UtensilsCrossed", "Dining"],
  ["ChefHat", "Kitchen"],
  ["Flame", "Stove / Heater"],
  ["Wind", "Fan"],
  ["Snowflake", "Air conditioning"],
  ["AirVent", "Air purifier"],
  ["Droplets", "Water"],
  ["ShowerHead", "Shower / Hot water"],
  ["Bath", "Bath / Jacuzzi"],
  ["BedDouble", "Bedding"],
  ["Refrigerator", "Fridge"],
  ["Microwave", "Microwave / Oven"],
  ["Coffee", "Coffee"],
  ["WashingMachine", "Laundry"],
  ["ParkingSquare", "Parking"],
  ["Car", "Car / Transport"],
  ["Bike", "Bike"],
  ["Dumbbell", "Gym"],
  ["Dog", "Pet friendly"],
  ["Baby", "Baby friendly"],
  ["CigaretteOff", "No smoking"],
  ["Cigarette", "Smoking"],
  ["Flower2", "Terrace / Balcony"],
  ["Trees", "Garden / Outdoors"],
  ["Leaf", "Eco / Plants"],
  ["Sparkles", "Housekeeping"],
  ["Lock", "Smart lock"],
  ["KeyRound", "Keyless entry"],
  ["Camera", "Security camera"],
  ["Speaker", "Speakers"],
  ["Music4", "Music"],
  ["Mic", "Microphone"],
  ["Gamepad2", "Games / Console"],
  ["Book", "Books / Library"],
  ["Monitor", "Monitor"],
  ["Laptop", "Workstation"],
  ["Briefcase", "Workspace"],
  ["Sun", "Natural light"],
  ["Cloud", "Weather"],
  ["Flashlight", "Emergency torch"],
  ["Zap", "Power backup"],
  ["MapPin", "Location"],
  ["HomeIcon", "Home"],
];

/**
 * iconByName — resolve a catalog key (e.g. "Wifi") to the Lucide component.
 * Falls back to Leaf if the name is unknown.
 */
export const iconByName = (name) => {
  if (!name) return null;
  if (name === "HomeIcon") return HomeIcon;
  return Icons[name] || null;
};

/**
 * amenityIcon — map a free-text amenity label (from Hostify or admin) to a
 * sensible Lucide icon. Case-insensitive substring matching so it works on
 * whatever the host typed. Falls back to a leaf icon for unmatched items
 * (nice neutral symbol rather than a square).
 */
const RULES = [
  [/(wi[-\s]?fi|internet|broadband)/i, Wifi],
  [/(smart\s?tv|netflix|roku|apple\s?tv|tv\b)/i, Tv],
  [/kitchen|cooking|chef/i, ChefHat],
  [/(dining|dinnerware|cutlery|utensils)/i, UtensilsCrossed],
  [/stove|gas|hob|induction|flame/i, Flame],
  [/(air\s?condition|a\/?c|cooling)/i, Snowflake],
  [/heat(er|ing)/i, Flame],
  [/fan\b|ceiling\s?fan/i, Wind],
  [/hot\s?water|geyser|shower/i, ShowerHead],
  [/bath|tub|jacuzzi/i, Bath],
  [/(parking|garage|valet)/i, ParkingSquare],
  [/coffee|nespresso|espresso|barista/i, Coffee],
  [/(gym|fitness|work[-\s]?out)/i, Dumbbell],
  [/pet|dog/i, Dog],
  [/baby|crib|infant|child/i, Baby],
  [/smoking|cigar/i, Cigarette],
  [/non[-\s]?smoking|no\s?smoking/i, CigaretteOff],
  [/(garden|plants|rooftop|terrace|balcony)/i, Flower2],
  [/bed|linen/i, BedDouble],
  [/laundry|washer|washing\s?machine|dryer/i, WashingMachine],
  [/(fridge|refrigerator)/i, Refrigerator],
  [/(microwave|oven)/i, Microwave],
  [/(projector|screen|cinema|home\s?theatre)/i, Tv2],
  [/(speaker|audio|sound\s?system)/i, Speaker],
  [/music|dj|spotify/i, Music4],
  [/camera|ring|doorbell/i, Camera],
  [/(cleaning|housekeeping|daily\s?clean)/i, Sparkles],
  [/(green|plant|eco|sustain)/i, Leaf],
  [/(lock|smart\s?lock|digital\s?lock)/i, Lock],
  [/(keyless|key|keypad)/i, KeyRound],
  [/(bike|cycle|scooter)/i, Bike],
  [/(car|taxi|transport|cab)/i, Car],
  [/(location|address|map)/i, MapPin],
  [/(work|office|desk|meeting)/i, Briefcase],
  [/(laptop|computer|workstation)/i, Laptop],
  [/(monitor|display)/i, Monitor],
  [/sun\s?light|natural\s?light|daylight/i, Sun],
  [/(backup|inverter|generator|power)/i, Zap],
  [/air\s?purifier|vent/i, AirVent],
  [/(torch|flashlight|emergency)/i, Flashlight],
  [/mic|podcast/i, Mic],
  [/book|library|reading/i, Book],
  [/(board\s?game|playstation|xbox|console|gam(es?|ing))/i, Gamepad2],
  [/(garden|tree|park|yard)/i, Trees],
  [/water|drink/i, Droplets],
  [/(iron|ironing)/i, HomeIcon],
];

export const amenityIcon = (labelOrItem) => {
  // Item may be a structured object { label, icon } (admin-picked icon) or a plain string.
  if (labelOrItem && typeof labelOrItem === "object" && labelOrItem.icon) {
    const chosen = iconByName(labelOrItem.icon);
    if (chosen) return chosen;
  }
  const s = typeof labelOrItem === "string"
    ? labelOrItem
    : (labelOrItem && labelOrItem.label) || "";
  for (const [re, Icon] of RULES) { if (re.test(s)) return Icon; }
  return Leaf;
};

/**
 * labelOf — extract the display string from a string OR {label, icon} item.
 * Used by rendering code that doesn't care about icons.
 */
export const labelOf = (item) =>
  typeof item === "string" ? item : (item && item.label) || "";
